import { useState } from 'react'

import type { Media } from '../../api/types'
import { dauerText, kannAbspielen } from '../../lib/video'
import { ReplyIcon } from '../../components/Icons'

/**
 * Ein Video im Verlauf.
 *
 * Zwei Fälle, und der zweite ist der Grund, warum es diese Datei gibt:
 *
 * 1. **Der Browser kann den Typ.** Dann steht hier ein Player mit Standbild.
 *    `preload="none"` ist dabei wichtig: ohne das lädt jedes Video im
 *    geöffneten Verlauf schon einmal seinen Kopf — bei zehn Videos am
 *    Mobilfunknetz merkt man das.
 * 2. **Der Browser kann den Typ nicht.** Ein iPhone nimmt in HEVC auf; Chrome
 *    und Firefox spielen das nicht ab. Statt eines Players, der schwarz
 *    bleibt und keine Erklärung gibt, steht dann das Standbild da, ein Satz
 *    dazu und ein Weg zur Datei.
 *
 * Umgewandelt wird nichts: ohne ffmpeg kann der Server es nicht, und im
 * Browser dauerte es Minuten je Video.
 */
export function VideoPlayer({
  media,
  onReply,
}: {
  media: Media
  /**
   * Auf genau dieses Video antworten.
   *
   * Nötig, sobald mehrere Videos in einer Nachricht stehen: der Bezug auf die
   * Nachricht wäre dann so unbestimmt wie bei vier Fotos. Der Server kann es
   * längst -- er nimmt jedes Medium an, das an der bezogenen Nachricht hängt.
   */
  onReply?: () => void
}) {
  const [gestartet, setGestartet] = useState(false)
  // Die Form des Kastens kommt vom Video selbst, sobald es sie kennt.
  //
  // Vorher stand hier `width`/`height` aus der Datenbank -- also eine
  // Behauptung über die Form, die niemand geprüft hatte. Eine iPhone-Aufnahme
  // liegt in der Datei immer quer und trägt die Drehung als Marke daneben; ob
  // ein Browser die gedrehte oder die rohe Größe meldet, ist von Fassung zu
  // Fassung verschieden. Ein Querformat-Video steckte deshalb in einem
  // hochkanten Rahmen mit schwarzen Balken.
  const [form, setForm] = useState<number | null>(
    media.width > 0 && media.height > 0 ? media.width / media.height : null,
  )
  const abspielbar = kannAbspielen(media.mime)

  // Der Knopf steht **über** dem Video und nicht darauf: die Fläche des
  // Players gehört seinen eigenen Bedienelementen, und auf einem Telefon liegt
  // ein Knopf in der Ecke genau dort, wo man zum Abspielen tippt.
  const antworten = onReply && (
    <button type="button" className="video__antwort" onClick={onReply}>
      <ReplyIcon size={14} />
      Auf dieses Video antworten
    </button>
  )

  if (!abspielbar) {
    return (
      <div className="video video--fremd">
        {media.poster_url && <img className="video__poster" src={media.poster_url} alt="" />}
        <p className="video__hinweis">
          Dieses Video ist in einem Format, das dieser Browser nicht abspielt
          (meist HEVC vom iPhone). Auf einem iPhone, iPad oder Mac läuft es.
        </p>
        {/* Bewusst ein Link und kein Download-Attribut: der Browser darf
            selbst entscheiden, ob er es speichert oder in einem eigenen Tab
            versucht -- manche können es dort doch. */}
        <a className="button button--outline" href={media.url} target="_blank" rel="noreferrer">
          Video öffnen{media.duration_ms > 0 ? ` (${dauerText(media.duration_ms)})` : ''}
        </a>
        {/* Gerade hier: wer das Video nicht abspielen kann, will vielleicht
            genau danach fragen. */}
        {antworten}
      </div>
    )
  }

  return (
    <div className="video">
      {antworten}
      <video
        className="video__player"
        // Ohne Standbild ein Ausweg, der ohne uns auskommt: `#t=0.1` sagt dem
        // Player, welche Stelle er zeigen soll. Er holt sich genau diesen
        // Ausschnitt über den Bereichsabruf -- und zeigt ein Bild statt einer
        // schwarzen Fläche, auch wenn das Standbild nie entstanden ist.
        src={media.poster_url === null ? `${media.url}#t=0.1` : media.url}
        poster={media.poster_url ?? undefined}
        controls
        // Ohne `playsInline` reisst iOS das Video in den Vollbildplayer, und
        // man ist aus dem Verlauf heraus.
        playsInline
        // `metadata` statt `none`, und das ist eine bewusste Abwägung: nur mit
        // dem Kopf der Datei kennt der Browser die wahre Form, **bevor**
        // jemand antippt -- sonst stünde bis dahin wieder ein Kasten da,
        // dessen Form geraten ist. Geholt werden dafür ein paar hundert
        // Kilobyte je Video, nicht das Video selbst; der Bereichsabruf sorgt
        // dafür, dass es dabei bleibt.
        preload="metadata"
        // Der gespeicherte Wert hält nur den Platz frei, damit der Verlauf
        // beim Laden nicht springt. Was gilt, sagt das Video selbst.
        style={form === null ? undefined : { aspectRatio: String(form) }}
        onLoadedMetadata={(event) => {
          const el = event.currentTarget
          if (el.videoWidth > 0 && el.videoHeight > 0) {
            setForm(el.videoWidth / el.videoHeight)
          }
        }}
        onPlay={() => setGestartet(true)}
      />
      {!gestartet && media.duration_ms > 0 && (
        <span className="video__dauer">{dauerText(media.duration_ms)}</span>
      )}
    </div>
  )
}
